import { pascalCase } from "pascal-case";
import type { ParsedUrlQuery } from "querystring";
import { pageUrl } from "utils/browserUtils";
import type { UseGTMProps } from "hooks/useGTM";
import { PAGE_VIEW_UA, PAGE_VIEW, DEFAULT_CURRENCY } from "./consts";
import type {
  TrackCustomEventProps,
  TrackUAEventProps,
  TrackEcommerceEventProps,
  TrackSearchEventProps,
} from "./utils.d";

const TRACKINGLESS_PARAM = "trackingless";

// Common utils

export const isPageTrackingless = (query: ParsedUrlQuery) => query && query.ver === TRACKINGLESS_PARAM;

export const initDataLayer = () => {
  if (!window.dataLayer) window.dataLayer = [];
};

const ecommerceEventReset = () => {
  if (window.dataLayer) window.dataLayer.push({ ecommerce: null });
};

// GA4 events tracking

export const trackCustomEvent = (event: TrackCustomEventProps) => {
  if (!event) return;
  initDataLayer();

  window.dataLayer.push(event);
};

export const trackEcommerceEvent = (event: TrackEcommerceEventProps) => {
  if (!event) return;

  initDataLayer();
  ecommerceEventReset();

  const { event: eventName, item_list_name: itemListName, item_list_id: itemListId, items } = event;

  window.dataLayer.push({
    event: eventName,
    ecommerce: {
      item_list_name: itemListName,
      item_list_id: itemListId,
      items,
    },
  });
};

export const trackSearchEvent = (event: TrackSearchEventProps) => {
  if (!event) return;

  const { event: eventName, type, searchPhrase, label } = event;
  initDataLayer();

  window.dataLayer.push({
    event: eventName,
    type,
    search_phrase: searchPhrase,
    label,
  });
};

export const trackPageView = (
  language: UseGTMProps["language"],
  pageReferrer: UseGTMProps["pageReferrer"],
) => {
  initDataLayer();

  window.dataLayer.push({
    event: PAGE_VIEW,
    page_currency: DEFAULT_CURRENCY,
    page_language: language,
    process: "",
    page_referrer: pageReferrer,
    page_type: "",
    // TBD:
    is_logged: "no",
    ota_brand: "",
  });
};

// UA events tracking (to be removed after GA4 migration)

export const trackUAEvent = (event: TrackUAEventProps) => {
  if (!event) return;
  initDataLayer();

  window.dataLayer.push({
    event: event[0],
    section: event[1],
    element: event[2],
    status: event[3],
  });
};

export const trackUAClickEvent = (section: string, element: string) => {
  if (!section || !element) return;

  trackUAEvent(["clicked", pascalCase(section), pascalCase(element)]);
};

const getPageViewedData = (
  language: UseGTMProps["language"],
  title: UseGTMProps["title"],
  path: UseGTMProps["path"]
) => {
  const pageViewedData = {
    locale: language,
    link: pageUrl(),
    title,
    pagePath: path,
  };

  return pageViewedData;
};

const pushPageViewUAEvent = (pageViewedData: UseGTMProps) => {
  const { locale, title, link, path } = pageViewedData;
  initDataLayer();

  window.dataLayer.push({
    event: PAGE_VIEW_UA,
    userAgent: navigator.userAgent,
    pageUrl: link,
    pagePath: path,
    pageTitle: title,
    pageLanguage: locale,
    pageCurrency: DEFAULT_CURRENCY,
    url: link,
    pageType: "",
  });
};

export const trackUAPageViewed = (
  language: UseGTMProps["language"],
  path: UseGTMProps["path"],
) => {
  const { title } = document;

  const pageViewedUAData = getPageViewedData(language, title, path);

  pushPageViewUAEvent(pageViewedUAData);
};
